// src/pages/ContinueInApp.js
//
// v2 – polished “continue in the app” handoff page
//
// Purpose:
// - principal completes registration on web
// - principal is guided cleanly into the mobile app journey
// - reminds them to use the same email + password they just created
// - includes QR code to current tester install URL
//
// Notes:
// - Replace EAS_INSTALL_URL with your current EAS/test install page
// - Keeps the flow customer-facing and reassuring
// - Designed to feel consistent with the RN onboarding first-impression stage

import React from "react";
import { useLocation, Link } from "react-router-dom";
import QRCode from "react-qr-code";

import { ROUTES } from "../config";
import "../styles/ContinueInApp.css";

// Replace with your current EAS/test install page URL
const EAS_INSTALL_URL = "https://expo.dev/accounts/limmy/projects/ghrianghraffy/builds/7a335c76-1a0e-4a73-bca8-3054b25842c0";

const ContinueInApp = () => {
  const location = useLocation();
  const emailFromState = location.state?.email || "";

  return (
    <div className="continue-in-app-page">
      <div className="continue-in-app-shell">
        <div className="continue-in-app-hero">
          <div className="continue-in-app-hero-top">
            <div className="continue-in-app-badge">Welcome</div>
            <div className="continue-in-app-progress">Next step: Mobile app</div>
          </div>

          <h1 className="continue-in-app-title">Your account is ready</h1>

          <p className="continue-in-app-subtitle">
            You’ve finished setting up your account on the website. The next step
            is to open the GhrianGhraffy mobile app and continue there.
          </p>

          <div className="continue-in-app-checklist">
            <div className="continue-in-app-checklist-item">
              <span className="continue-in-app-check">✓</span>
              <span>Your account is now active</span>
            </div>
            <div className="continue-in-app-checklist-item">
              <span className="continue-in-app-check">✓</span>
              <span>You’ll use the same login details in the app</span>
            </div>
            <div className="continue-in-app-checklist-item">
              <span className="continue-in-app-check">✓</span>
              <span>Your school setup will continue inside the app</span>
            </div>
          </div>
        </div>

        <div className="continue-in-app-grid">
          <section className="continue-in-app-card">
            <div className="continue-in-app-step-pill">Step 1</div>
            <h2 className="continue-in-app-card-title">Use the same login details</h2>
            <p className="continue-in-app-card-subtitle">
              Please sign in on the mobile app with the same credentials you just created here.
            </p>

            <div className="continue-in-app-credential-box">
              <div className="continue-in-app-credential-row">
                <div className="continue-in-app-credential-label">Email</div>
                <div className="continue-in-app-credential-value">
                  {emailFromState || "Use the email address you registered with"}
                </div>
              </div>

              <div className="continue-in-app-credential-row">
                <div className="continue-in-app-credential-label">Password</div>
                <div className="continue-in-app-credential-value">
                  The password you just created a moment ago
                </div>
              </div>
            </div>

            <p className="continue-in-app-note">
              You will not need a separate app-only account.
            </p>
          </section>

          <section className="continue-in-app-card">
            <div className="continue-in-app-step-pill">Step 2</div>
            <h2 className="continue-in-app-card-title">Install the mobile app</h2>
            <p className="continue-in-app-card-subtitle">
              Scan the QR code with your phone camera, or open the test install page directly.
            </p>

            <div className="continue-in-app-qr-block">
              <div className="continue-in-app-qr-frame">
                <QRCode value={EAS_INSTALL_URL} size={176} />
              </div>

              <div className="continue-in-app-qr-actions">
                <a
                  className="continue-in-app-button primary"
                  href={EAS_INSTALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open install page
                </a>

                <p className="continue-in-app-helper">
                  This currently points to the tester install page while the app is still in testing.
                </p>
              </div>
            </div>
          </section>
        </div>

        <section className="continue-in-app-footer-card">
          <div className="continue-in-app-step-pill">Step 3</div>
          <h2 className="continue-in-app-card-title">Continue in the app</h2>
          <p className="continue-in-app-card-subtitle">
            Once the app is installed, log in and complete your school setup there.
            After that, you’ll be brought into your Principal experience.
          </p>

          <div className="continue-in-app-footer-actions">
            <a
              className="continue-in-app-button primary"
              href={EAS_INSTALL_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Install on my phone
            </a>

            <Link className="continue-in-app-button secondary" to={ROUTES.LOGIN}>
              Back to Login
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContinueInApp;